import type {
    IBusinessTelHours,
    IBusinessTelHoursItem,
    ICalculatorSettings,
    IContact,
    IEbankingPortalSettings,
    IEservices,
    IFooter,
    IFooterColumn,
    IGeneral,
    IHeader,
    IHeaderImageMetaData,
    IL10nString,
    IRelationshipType,
    ISettings,
    ISettingsKeystoreEntry,
    ITeaser,
    TSettingsPath,
    TSettingsValueType,
} from '../api/domain';
import type { IProductNotificationSettings } from '../api/domain/settings/product-notification-settings';
import type { IProductCategory, ProductCategoryIcon } from '../enum/product-category';
import type { IProductSettings } from '../api/domain/settings/product-settings';
import type { IMasterData } from '../api/domain/settings/master-data';

const getListLatestIndex = (settings: ISettingsKeystoreEntry[], objectKey: string) => {
    let size = 0;
    const regex = new RegExp(`${objectKey}\\[(\\d+)\\]`);
    settings.forEach((setting) => {
        const regexResult = regex.exec(setting.path);
        if (regexResult && parseInt(regexResult[1]) > size) {
            size = parseInt(regexResult[1]);
        }
    });

    return size;
};

const getTranslatedValuesFromKeystoreParams = (settings: ISettingsKeystoreEntry[]) => {
    const translationMap = {
        it: new Map<string, TSettingsValueType>(),
        en: new Map<string, TSettingsValueType>(),
        fr: new Map<string, TSettingsValueType>(),
        de: new Map<string, TSettingsValueType>(),
    };

    translationMap.it.set('languageCode', 'it');
    translationMap.en.set('languageCode', 'en');
    translationMap.de.set('languageCode', 'de');
    translationMap.fr.set('languageCode', 'fr');

    settings.forEach((setting) => {
        const pathSegments = setting.path.split('.');
        const key = pathSegments[pathSegments.length - 1];
        if (setting.languageCode && typeof setting.value === 'string') {
            (translationMap[setting.languageCode] as Map<string, TSettingsValueType> | undefined)?.set(key, setting.value);
        }
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.values(translationMap)
        .filter((val) => val.size > 1)
        .map((val) => Object.fromEntries(val)) as any;
};

const getPicturesFromRaw = (settings: ISettingsKeystoreEntry[]) => {
    const lastPictureIndex = getListLatestIndex(settings, 'pictures');
    const pictures: any[] = [];

    for (let picture = 0; picture <= lastPictureIndex; picture++) {
        pictures.push({
            id: settings.find((setting) => setting.path === `header.pictures[${picture}].id`)?.value,
            default: settings.find((setting) => setting.path === `header.pictures[${picture}].default`)?.value === 'true',
            text: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes(`header.pictures[${picture}].text.`))),
        });
    }

    return pictures as IHeaderImageMetaData[];
};

const getColumns = (settings: ISettingsKeystoreEntry[]) => {
    if (!settings.length) {
        return [];
    }

    const lastColumnIndex = getListLatestIndex(settings, 'columns');
    const columns: any[] = [];

    for (let column = 0; column <= lastColumnIndex; column++) {
        const filteredSettings = settings.filter((setting) => setting.path.includes(`footer.columns[${column}]`));
        const lastPropertyIndex = getListLatestIndex(filteredSettings, 'properties');
        const properties: any[] = [];

        for (let property = 0; property <= lastPropertyIndex; property++) {
            properties.push(
                getTranslatedValuesFromKeystoreParams(
                    filteredSettings.filter((setting) => setting.path.includes(`footer.columns[${column}].properties[${property}].`)),
                ),
            );
        }

        columns.push({
            title: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes(`footer.columns[${column}].title.`))),
            properties,
        });
    }

    return columns as IFooterColumn[];
};

const getSocialmedia = (settings: ISettingsKeystoreEntry[]) => {
    return {
        facebook: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('facebook'))),
        twitter: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('twitter'))),
        youtube: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('youtube'))),
        instagram: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('instagram'))),
        linkedin: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('linkedin'))),
    };
};

const getLinks = (settings: ISettingsKeystoreEntry[]) => {
    return {
        agb: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('agb'))),
        dsgvo: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('dsgvo'))),
        legal: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('legal'))),
    };
};

const getBusinessTelHours = (settings: ISettingsKeystoreEntry[]): IBusinessTelHours[] => {
    if (!settings.length) {
        return [];
    }

    const lastItemsIndex = getListLatestIndex(settings, 'items');
    const businessTelHours: IBusinessTelHours[] = [];
    const languages = ['en', 'de', 'it', 'fr'];

    languages.forEach((languageCode) => {
        const filteredSettings = settings.filter((setting) => setting.languageCode === languageCode);
        if (!filteredSettings.length) {
            return;
        }
        const items: IBusinessTelHoursItem[] = [];
        for (let itemCount = 0; itemCount <= lastItemsIndex; itemCount++) {
            const title = (filteredSettings.find((setting) => setting.path === `contact.businessTelHours.items[${itemCount}].title`)?.value as string) ?? '';
            const number = (filteredSettings.find((setting) => setting.path === `contact.businessTelHours.items[${itemCount}].number`)?.value as string) ?? '';
            const hours = (filteredSettings.find((setting) => setting.path === `contact.businessTelHours.items[${itemCount}].hours`)?.value as string) ?? '';
            items.push({ title, number, hours });
        }

        businessTelHours.push({ languageCode, items });
    });

    return businessTelHours;
};

const getRelationshipTypes = (settings: ISettingsKeystoreEntry[]): IRelationshipType[] => {
    if (!settings.length) {
        return [];
    }

    const lastRelationshipTypesIndex = getListLatestIndex(settings, 'relationshipTypes');
    const relationshipTypes: IRelationshipType[] = [];
    for (let itemCount = 0; itemCount <= lastRelationshipTypesIndex; itemCount++) {
        relationshipTypes.push({
            finnovaId:
                (settings.find((setting) => setting.path.includes(`eservices.powerOfAttorney.relationshipTypes[${itemCount}].finnovaId`))?.value as string) ??
                '',
            title: getTranslatedValuesFromKeystoreParams(
                settings.filter((setting) => setting.path.includes(`eservices.powerOfAttorney.relationshipTypes[${itemCount}].title.`)),
            ),
        });
    }

    return relationshipTypes;
};

const getGeneralSettings = (settings: ISettingsKeystoreEntry[]): IGeneral => {
    const name = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('general.name.')));
    const logoAltText = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('general.logoAltText.')));

    return {
        name,
        logoAltText,
    };
};

const getHeaderSettings = (settings: ISettingsKeystoreEntry[]): IHeader => {
    /** @deprecated */
    // const overlapLogo = settings.find((setting) => setting.path === 'header.overlapLogo')?.value === 'true';
    const pictures = getPicturesFromRaw(settings.filter((setting) => setting.path.includes('header.pictures')));
    const tabTitle = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.tabTitle.')));
    const portalTitle = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.portalTitle.')));
    const ebanking = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.ebanking.')));
    const docstore = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.docstore.')));
    const inbox = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.inbox.')));
    const eservices = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.eservices.')));
    const etools = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.etools.')));
    const products = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.products.')));
    const bankLogoExternalUrl = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('header.bankLogoExternalUrl.')));
    const useBankLogoExternalUrl = settings.find((setting) => setting.path === 'header.useBankLogoExternalUrl')?.value === 'true';

    return {
        /** @deprecated */
        // overlapLogo,
        pictures,
        tabTitle,
        portalTitle,
        ebanking,
        docstore,
        inbox,
        eservices,
        etools,
        products,
        default: false,
        login: [], //TODO: update
        logout: [], //TODO: update
        picture: '', //TODO: update
        text: [], //TODO: update
        bankLogoExternalUrl,
        useBankLogoExternalUrl,
    };
};

const getFooterSettings = (settings: ISettingsKeystoreEntry[]): IFooter => {
    const address = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('footer.address.')));
    const columns = getColumns(settings.filter((setting) => setting.path.includes('footer.columns')));
    const socialmedia = getSocialmedia(settings.filter((setting) => setting.path.includes('footer.socialmedia')));
    const links = getLinks(settings.filter((setting) => setting.path.includes('footer.links')));
    const productTitle = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('footer.productTitle.')));

    return {
        address,
        columns,
        socialmedia,
        links,
        productTitle,
    };
};

const getTopics = (settings: ISettingsKeystoreEntry[]): IL10nString[][] => {
    if (!settings.length) {
        return [];
    }

    const lastTopicsIndex = getListLatestIndex(settings, 'topics');
    const topics: IL10nString[][] = [];
    for (let itemCount = 0; itemCount <= lastTopicsIndex; itemCount++) {
        topics.push(getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes(`contact.topics[${itemCount}]`))));
    }

    return topics;
};

const getContactSettings = (settings: ISettingsKeystoreEntry[]): IContact => {
    const phone = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('contact.phone.')));
    const location = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('contact.location.')));
    const businessTelHours = getBusinessTelHours(settings.filter((setting) => setting.path.includes('contact.businessTelHours')));
    const useInternationalPhoneFormat = settings.find((setting) => setting.path === 'contact.useInternationalPhoneFormat')?.value === 'true';
    const displayRM = settings.find((setting) => setting.path === 'contact.displayRM')?.value === 'true';
    const displayRMPhone = settings.find((setting) => setting.path === 'contact.displayRMPhone')?.value === 'true';
    const usePhoneOverview = settings.find((setting) => setting.path === 'contact.usePhoneOverview')?.value === 'true';
    const appointmentAtHomeForCompany = settings.find((setting) => setting.path === 'contact.appointmentAtHomeForCompany')?.value === 'true';
    const appointmentAtHomeForPrivate = settings.find((setting) => setting.path === 'contact.appointmentAtHomeForPrivate')?.value === 'true';
    const topics = getTopics(settings.filter((setting) => setting.path.includes('contact.topics')));
    const enableVideoAppointment = settings.find((setting) => setting.path === 'contact.enableVideoAppointment')?.value === 'true';
    const videoAppointmentLabel = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('contact.videoAppointmentLabel.')));

    return {
        phone,
        location,
        businessTelHours,
        useInternationalPhoneFormat,
        displayRM,
        displayRMPhone,
        usePhoneOverview,
        appointmentAtHomeForCompany,
        appointmentAtHomeForPrivate,
        topics,
        enableVideoAppointment,
        videoAppointmentLabel,
    };
};

const getCalculatorNotificationSettings = (settings: ISettingsKeystoreEntry[]) => {
    const notificationTextKeystoreEntries = settings.filter((setting) => setting.path.includes('calculator.notificationSettings.text'));
    const selfRegistrationLinkLabelKeystoreEntries = settings.filter((setting) =>
        setting.path.includes('calculator.notificationSettings.selfRegistrationLinkLabel'),
    );
    const notificationEnabledKeystoreEntry = settings.find((setting) => setting.path.includes('calculator.notificationSettings.enabled'));
    const showSelfRegistrationLinkKeystoreEntry = settings.find((setting) => setting.path.includes('calculator.notificationSettings.showSelfRegistrationLink'));

    if (notificationTextKeystoreEntries.length && notificationEnabledKeystoreEntry && showSelfRegistrationLinkKeystoreEntry) {
        const notificationText = getTranslatedValuesFromKeystoreParams(notificationTextKeystoreEntries);
        const selfRegistrationLinkLabel = getTranslatedValuesFromKeystoreParams(selfRegistrationLinkLabelKeystoreEntries);
        const notificationEnabled = notificationEnabledKeystoreEntry?.value !== 'false';
        const showSelfRegistrationLink = showSelfRegistrationLinkKeystoreEntry?.value !== 'false';

        return {
            enabled: notificationEnabled,
            text: notificationText,
            selfRegistrationLinkLabel,
            showSelfRegistrationLink,
        };
    }

    return undefined;
};

const getCalculatorSettings = (settings: ISettingsKeystoreEntry[]): ICalculatorSettings => {
    const showAffordability = settings.find((setting) => setting.path.includes('calculator.showAffordability'))?.value !== 'false';
    const showSaving3A = settings.find((setting) => setting.path.includes('calculator.showSaving3A'))?.value !== 'false';
    const showSaving = settings.find((setting) => setting.path === 'calculator.showSaving')?.value !== 'false';
    const showBudget = settings.find((setting) => setting.path.includes('calculator.showBudget'))?.value !== 'false';
    const showIncomeTax = settings.find((setting) => setting.path.includes('calculator.showIncomeTax'))?.value !== 'false';
    const showCapitalTax = settings.find((setting) => setting.path.includes('calculator.showCapitalTax'))?.value !== 'false';
    const showPkPurchase = settings.find((setting) => setting.path.includes('calculator.showPkPurchase'))?.value !== 'false';
    const showSimpleInheritance = settings.find((setting) => setting.path.includes('calculator.showSimpleInheritance'))?.value !== 'false';
    const showRisk = settings.find((setting) => setting.path.includes('calculator.showRisk'))?.value !== 'false';
    const showAmortizationComparison = settings.find((setting) => setting.path.includes('calculator.showAmortizationComparison'))?.value !== 'false';

    return {
        showAffordability,
        showSaving3A,
        showSaving,
        showBudget,
        showIncomeTax,
        showCapitalTax,
        showPkPurchase,
        showSimpleInheritance,
        showRisk,
        showAmortizationComparison,
        notificationSettings: getCalculatorNotificationSettings(settings),
    };
};

const getShowTeasers = (settings: ISettingsKeystoreEntry[]) => settings.find((setting) => setting.path === 'showTeasers')?.value === 'true';

const getTeasersSettings = (settings: ISettingsKeystoreEntry[]): ITeaser[] => {
    const teasersRaw = settings.filter((setting) => setting.path.includes('teasers['));
    const teasersNumber = teasersRaw.filter((setting) => setting.path.includes('.id')).length;
    const teasers: ITeaser[] = [];

    for (let teaserCount = 0; teaserCount < teasersNumber; teaserCount++) {
        teasers.push({
            id: (settings.find((setting) => setting.path === `teasers[${teaserCount}].id`)?.value as string) ?? '',
            text: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes(`teasers[${teaserCount}].text.`))),
        });
    }

    return teasers;
};

const getShowHintMessageOnCardOrder = (settings: ISettingsKeystoreEntry[]) =>
    settings.find((setting) => setting.path === 'eservices.powerOfAttorney.showHintMessageOnCardOrder')?.value === 'true';

const getShowOrdersLink = (settings: ISettingsKeystoreEntry[]) =>
    settings.find((setting) => setting.path === 'eservices.orders.showOrdersLink')?.value === 'true';

const getMasterDataHints = (settings: ISettingsKeystoreEntry[]): IMasterData => ({
    hintAddress: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('eservices.masterData.hintAddress'))),
    hintMaritalStatus: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('eservices.masterData.hintMaritalStatus'))),
    hintName: getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('eservices.masterData.hintName'))),
    showHintAddress: settings.find((setting) => setting.path.includes('eservices.masterData.showHintAddress'))?.value === 'true',
    showHintMaritalStatus: settings.find((setting) => setting.path.includes('eservices.masterData.showHintMaritalStatus'))?.value === 'true',
    showHintName: settings.find((setting) => setting.path.includes('eservices.masterData.showHintName'))?.value === 'true',
});

const getEServicesSetting = (settings: ISettingsKeystoreEntry[]): IEservices => {
    const relationshipTypes = getRelationshipTypes(settings.filter((setting) => setting.path.includes('eservices.powerOfAttorney.relationshipTypes')));
    const showHintMessageOnCardOrder = getShowHintMessageOnCardOrder(settings);
    const showOrdersLink = getShowOrdersLink(settings);
    const ordersLinks = getTranslatedValuesFromKeystoreParams(settings.filter((setting) => setting.path.includes('eservices.orders.ordersLinks.'))) || [];
    const filterNonPrivatePowersOfAttorney =
        settings.find((setting) => setting.path.includes('eservices.powerOfAttorney.filterNonPrivatePowersOfAttorney'))?.value === 'true';

    const hints: IMasterData = getMasterDataHints(settings);

    return {
        powerOfAttorney: { relationshipTypes, showHintMessageOnCardOrder, filterNonPrivatePowersOfAttorney },
        masterData: { ...hints },
        orders: { ordersLinks, showOrdersLink },
    };
};

const getCustomProductCategories = (settings: ISettingsKeystoreEntry[]): IProductCategory[] => {
    const filteredSettings = settings.filter((setting) => setting.path.includes('customProductCategories'));

    if (!filteredSettings.length) return [];

    const lastCategoryIndex = getListLatestIndex(filteredSettings, 'customProductCategories');
    const categories: IProductCategory[] = [];

    for (let categoryIndex = 0; categoryIndex <= lastCategoryIndex; categoryIndex++) {
        categories.push({
            categoryId: filteredSettings.find((setting) => setting.path.includes(`customProductCategories[${categoryIndex}].categoryId`))?.value as string,
            icon: filteredSettings.find((setting) => setting.path.includes(`customProductCategories[${categoryIndex}].icon`))?.value as ProductCategoryIcon,
            title: getTranslatedValuesFromKeystoreParams(
                filteredSettings.filter((setting) => setting.path.includes(`customProductCategories[${categoryIndex}].title.`)),
            ),
            priority: filteredSettings.find((setting) => setting.path.includes(`customProductCategories[${categoryIndex}].priority`))?.value as number,
        });
    }

    return categories;
};

const getProductNotificationSettings = (settings: ISettingsKeystoreEntry[]): IProductNotificationSettings | undefined => {
    const notificationTextKeystoreEntries = settings.filter((setting) => setting.path.includes('productNotification.text'));
    const notificationEnabledKeystoreEntry = settings.find((setting) => setting.path.includes('productNotification.enabled'));

    if (notificationTextKeystoreEntries.length && notificationEnabledKeystoreEntry) {
        const notificationText = getTranslatedValuesFromKeystoreParams(notificationTextKeystoreEntries);
        const notificationEnabled = notificationEnabledKeystoreEntry?.value !== 'false';

        return {
            enabled: notificationEnabled,
            text: notificationText,
        };
    }

    return undefined;
};

const getEBankingPortalSettings = (settings: ISettingsKeystoreEntry[]): IEbankingPortalSettings => {
    const eBankingIFrameEnabledKeystoreEntry = settings.find((setting) => setting.path.includes('eBankingPortal.eBankingIFrameEnabled'));

    const eBankingIFrameEnabled = eBankingIFrameEnabledKeystoreEntry?.value !== 'false';

    return {
        eBankingIFrameEnabled,
    };
};

const getProductSettings = (settings: ISettingsKeystoreEntry[]): IProductSettings => {
    const enableFeedbackFunctionKeystoreEntry = settings.find((setting) => setting.path.includes('productSettings.enableFeedbackFunction'));
    const enableFeedbackFunctionKeystoreEntryFallback = 'true';
    const enableFeedbackFunction = (enableFeedbackFunctionKeystoreEntry?.value ?? enableFeedbackFunctionKeystoreEntryFallback) === 'true';

    return {
        enableFeedbackFunction,
    };
};

export const parseToSettings = (settings: ISettingsKeystoreEntry[]): ISettings => {
    return {
        general: getGeneralSettings(settings),
        header: getHeaderSettings(settings),
        footer: getFooterSettings(settings),
        contact: getContactSettings(settings),
        calculator: getCalculatorSettings(settings),
        showTeasers: getShowTeasers(settings),
        teasers: getTeasersSettings(settings),
        eservices: getEServicesSetting(settings),
        customProductCategories: getCustomProductCategories(settings),
        productNotification: getProductNotificationSettings(settings),
        productSettings: getProductSettings(settings),
        eBankingPortal: getEBankingPortalSettings(settings),
    };
};

let settingsKeyValueMap: ISettingsKeystoreEntry[] = [];

const parseTranslations = (property: any, settingsVersionMap: Record<string, number>, path: string) => {
    Object.keys(property)
        .filter((key) => key !== 'languageCode')
        .forEach((key) => {
            if (property[key] instanceof Array) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                property[key]?.forEach((setting: any, idx: number) => {
                    parseTranslations({ ...setting, languageCode: property.languageCode }, settingsVersionMap, `${path}.${key}[${idx}]`);
                });
            } else {
                settingsKeyValueMap.push({
                    path: `${path}.${key}` as TSettingsPath,
                    value: property[key],
                    languageCode: property.languageCode,
                    version: settingsVersionMap[`${path}.${key}-${property.languageCode}`],
                });
            }
        });
};

const parseSettingsToMap = (property: Partial<ISettings>, settingsVersionMap: Record<string, number>, path = '') => {
    Object.keys(property).forEach((key) => {
        const propertyKeys = Object.keys(property[key]);
        if (typeof property[key] !== 'object') {
            const builtPath = path === '' ? key : `${path}.${key}`;
            settingsKeyValueMap.push({
                path: builtPath as TSettingsPath,
                value: String(property[key]),
                languageCode: '',
                version: settingsVersionMap[`${path}.${key}`],
            });
        } else if (propertyKeys.includes('languageCode')) {
            parseTranslations(property[key], settingsVersionMap, path);
        } else if (key.match(/\d+/g)) {
            parseSettingsToMap(property[key], settingsVersionMap, `${path}[${key}]`);
        } else {
            //eslint-disable-next-line  @typescript-eslint/no-unused-expressions
            path ? parseSettingsToMap(property[key], settingsVersionMap, `${path}.${key}`) : parseSettingsToMap(property[key], settingsVersionMap, `${key}`);
        }
    });
};

export const getKeyValueEntriesFromSettings = (setting: Partial<ISettings>, settingsVersionMap: Record<string, number>): ISettingsKeystoreEntry[] => {
    settingsKeyValueMap = [];
    parseSettingsToMap(setting, settingsVersionMap);

    return settingsKeyValueMap;
};
